import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, SectionCard, Topbar, LoadingScreen, EmptyState
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const STATUSES = ['all','pending','verified','resolved','rejected'];

const ROLE_COLORS = {
  student: { bg: 'rgba(16,185,129,0.2)', text: '#6ee7b7' },
  faculty: { bg: 'rgba(99,102,241,0.2)', text: '#a5b4fc' },
};

function StatusBar({ s }) {
  const segs = [
    { label:'Pending',   count:s.pending,   color:COLORS.amber  },
    { label:'Verified',  count:s.verified,  color:COLORS.blue   },
    { label:'Resolved',  count:s.resolved,  color:COLORS.green  },
    { label:'Rejected',  count:s.rejected,  color:COLORS.red    },
    { label:'Escalated', count:s.escalated, color:COLORS.orange },
  ].filter(x => x.count > 0);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', height:14, borderRadius:8, overflow:'hidden', gap:2, marginBottom:10 }}>
        {segs.length === 0
          ? <div style={{ flex:1, background:'rgba(255,255,255,0.1)', borderRadius:8 }} />
          : segs.map(x => (
            <div key={x.label} title={`${x.label}: ${x.count}`}
              style={{ flex:x.count, background:x.color, borderRadius:4, transition:'flex 0.5s' }} />
          ))
        }
      </div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
        {[
          ['Total',    s.total,    COLORS.purple],
          ['Pending',  s.pending,  COLORS.amber],
          ['Verified', s.verified, COLORS.blue],
          ['Resolved', s.resolved, COLORS.green],
          ['Rejected', s.rejected, COLORS.red],
          ['Escalated',s.escalated,COLORS.orange],
        ].map(([l,v,c]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:c }} />
            <span style={{ fontSize:12, color:'rgba(255,255,255,0.7)' }}>
              {l} <strong style={{ color:'#fff' }}>{v}</strong>
              <span style={{ color:'rgba(255,255,255,0.4)', marginLeft:3 }}>
                ({Math.round((v/(s.total||1))*100)}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComplaintRow({ c, onView, index }) {
  const statusColor = {
    pending:COLORS.amber, verified:COLORS.blue,
    resolved:COLORS.green, rejected:COLORS.red,
  };
  return (
    <tr className="ur-row-hover" onClick={() => onView(c.id)}
      style={{ animation:'fadeUp 0.3s ease-out both', animationDelay:(index*0.02)+'s' }}>
      <td style={{ padding:'10px 14px', color:'#fff', fontWeight:600, fontSize:13 }}>
        <div>{c.title}</div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', marginTop:2 }}>By: {c.student_name}</div>
      </td>
      <td style={{ padding:'10px 14px' }}>
        <span style={{
          fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:700,
          background:(statusColor[c.status]||COLORS.purple)+'22',
          color:statusColor[c.status]||COLORS.purple,
          border:'1px solid '+(statusColor[c.status]||COLORS.purple)+'44',
        }}>{c.status}</span>
      </td>
      <td style={{ padding:'10px 14px', fontSize:12, color:'rgba(255,255,255,0.6)' }}>
        {c.department || c.university_category || '—'}
      </td>
      <td style={{ padding:'10px 14px', fontSize:12, color:'rgba(255,255,255,0.5)' }}>
        {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
      </td>
      <td style={{ padding:'10px 14px', fontSize:12, color:'rgba(255,255,255,0.5)' }}>
        {c.resolved_at ? new Date(c.resolved_at).toLocaleDateString() : '—'}
      </td>
      <td style={{ padding:'10px 14px' }}>
        {c.escalation_level > 0 && (
          <span style={{ fontSize:11, color:COLORS.orange, fontWeight:700 }}>Escalated</span>
        )}
      </td>
    </tr>
  );
}

function ComplaintsTable({ complaints, onView }) {
  if (!complaints.length)
    return <EmptyState text="No complaints found" sub="Try adjusting your filters" />;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
            {['Complaint','Status','Dept / Category','Submitted','Resolved',''].map(h => (
              <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:11,
                fontWeight:700, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:0.8 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {complaints.map((c, i) => (
            <ComplaintRow key={c.id} c={c} index={i} onView={onView} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminComplaintsHistoryInner() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [complaints, setComplaints] = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]         = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter]   = useState('');
  const [roleTab, setRoleTab]         = useState('student');

  const now = new Date();
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  const fetchComplaints = useCallback(() => {
    setLoading(true);
    let url = '/admin-reports/complaints?';
    if (statusFilter !== 'all') url += 'status=' + statusFilter + '&';
    if (search.trim()) url += 'search=' + encodeURIComponent(search.trim()) + '&';
    if (monthFilter && yearFilter) url += 'month=' + monthFilter + '&year=' + yearFilter + '&';
    api.get(url)
      .then(res => {
        setComplaints(res.data.complaints || []);
        setSummary(res.data.summary || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, search, monthFilter, yearFilter]);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  const isUni = user?.admin_type === 'university';
  const scope = isUni ? 'University-wide complaints' : ((user?.department || '') + ' — dept complaints');

  const studentComplaints = complaints.filter(c => c.submitter_role === 'student');
  const facultyComplaints = complaints.filter(c => c.submitter_role === 'faculty');
  const activeList = roleTab === 'student' ? studentComplaints : facultyComplaints;

  const inp = {
    padding:'8px 12px', borderRadius:9, fontSize:13,
    background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)',
    color:'#fff', outline:'none',
  };

  const tabStyle = (active, role) => {
    const rc = ROLE_COLORS[role];
    return {
      padding: '6px 18px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
      border: `1px solid ${active ? rc.text : 'rgba(255,255,255,0.15)'}`,
      background: active ? rc.bg : 'rgba(255,255,255,0.05)',
      color: active ? rc.text : 'rgba(255,255,255,0.55)',
      transition: 'all 0.2s',
    };
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid}/><div style={s.bgOrb1}/><div style={s.bgOrb2}/><div style={s.bgOrb3}/>
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition:'margin-left 0.3s ease' }}>
          <Topbar
            title="Complaints History"
            subtitle={'Complete complaint archive — ' + scope}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <div style={{ display:'flex', gap:8 }}>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/admin/report')}>
                  Monthly Report
                </button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/dashboard')}>
                  Dashboard
                </button>
              </div>
            }
          />
          <div style={s.content}>

            {summary && (
              <SectionCard title="Overall Statistics" subtitle={scope} delay="0s">
                <StatusBar s={summary} />
              </SectionCard>
            )}

            <SectionCard
              title="All Complaints"
              subtitle="Filter and search your complete complaint archive"
              delay="0.1s"
              action={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>
                    {activeList.length} result{activeList.length !== 1 ? 's' : ''}
                  </span>
                  <button style={tabStyle(roleTab === 'student', 'student')} onClick={() => setRoleTab('student')}>
                    Students ({studentComplaints.length})
                  </button>
                  <button style={tabStyle(roleTab === 'faculty', 'faculty')} onClick={() => setRoleTab('faculty')}>
                    Faculty ({facultyComplaints.length})
                  </button>
                </div>
              }
            >
              {/* Filter bar */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {STATUSES.map(st => (
                    <button key={st} onClick={() => setStatusFilter(st)}
                      className="ur-btn-hover"
                      style={{
                        padding:'6px 14px', borderRadius:20, fontSize:12, cursor:'pointer',
                        fontWeight:600, border:'1px solid rgba(255,255,255,0.2)',
                        background: statusFilter === st ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.07)',
                        color: statusFilter === st ? '#c4b5fd' : 'rgba(255,255,255,0.7)',
                      }}>
                      {st.charAt(0).toUpperCase() + st.slice(1)}
                    </button>
                  ))}
                </div>
                <input style={{ ...inp, flex:1, minWidth:160 }}
                  placeholder="Search by title or description..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchComplaints()}
                />
                <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} style={inp}>
                  <option value="" style={{ background:'#1e293b' }}>All months</option>
                  {MONTHS.map((m,i) => <option key={i} value={i+1} style={{ background:'#1e293b' }}>{m}</option>)}
                </select>
                <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} style={inp}>
                  <option value="" style={{ background:'#1e293b' }}>All years</option>
                  {years.map(y => <option key={y} value={y} style={{ background:'#1e293b' }}>{y}</option>)}
                </select>
                <button className="ur-btn-hover" style={s.btnPrimary} onClick={fetchComplaints}>Search</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => {
                  setStatusFilter('all'); setSearch(''); setMonthFilter(''); setYearFilter('');
                }}>Reset</button>
              </div>

              {loading
                ? <div style={{ padding:'30px 0', textAlign:'center', color:'rgba(255,255,255,0.4)' }}>Loading...</div>
                : <ComplaintsTable complaints={activeList} onView={id => navigate('/complaints/' + id)} />
              }
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminComplaintsHistory() {
  return <ThemeProvider><AdminComplaintsHistoryInner /></ThemeProvider>;
}
